/*-- npm packages --*/
import React, { useContext } from "react";
import { graphql, useStaticQuery } from "gatsby";

/*-- component molecules --*/
import VenueHero from "../molecules/VenuePageSections/VenueHero.component";
import VenueInfo from "../molecules/VenuePageSections/VenueInfo.component";
import VenueClubCards from "../molecules/VenuePageSections/VenueClubCards.component";
import VenueClubSchedule from "../molecules/VenuePageSections/VenueClubSchedule.component";
import VenueAvailabilityChecker from "../molecules/VenuePageSections/VenueAvailabilityChecker.component";
import VenueNearbyVenues from "../molecules/VenuePageSections/VenueNearbyVenues.component";

/*-- component utils --*/
import DynamicComponent from "../utils/DynamicComponent/DynamicComponent.component";

/*-- styles --*/
import { layoutClassNames } from "../../styles/ClassNames/global.classes";

/*-- utils --*/
import renderChildren from "../../utils/renderBloks.util";
import BaseDataLayer from "../utils/BaseDataLayer/BaseDataLayer.util";
import getBlok from "../../utils/getBlok/getBlok.util";
import mapStoryblokVenue from "../../utils/mapStoryblokVenue/mapStoryblokVenue.util";

/*-- interfaces --*/
import { StoryblokAsset } from "../../interfaces/Storyblok/StoryblokAsset.interface";
import TemplateProps from "../../interfaces/TemplateProps.interface";
import Blok from "../../interfaces/Storyblok/Blok.interface";
import { ServiceCardProps } from "../../interfaces/VenuePage/Services.interface";
import SEO from "../../interfaces/SEO.interface";
import { StoryblokHardcodedVenue } from "../../interfaces/Storyblok/StoryblokHardcodedVenue.interface";

/*-- contexts --*/
import VenuesContext, { Venue } from "../../contexts/VenuesContext";
import FiltersContext from "../../contexts/FiltersContext";
import VenueNoDataFound from "../molecules/VenuePageSections/VenueNoDataFound.component";
import HolidayProgramSchedule from "../molecules/VenuePageSections/HolidayProgramSchedule.component";
import { ButtonProps } from "../../interfaces/Button.interface";
import LayoutContext from "../../contexts/LayoutContext";
import BreadcrumbBar from "../bloks/BreadcrumbBar/BreadcrumbBar";
import SampleFeesTable from "../bloks/SampleFeesTable/SampleFeesTable.blok";
import { SiteEnvironments } from "../../interfaces/enums/SiteEnvironments";
import ZigZagContentLight from "../bloks/ZigZagContent/ZigZagContentLight.blok";

const VenuePageTemplate: React.FC<TemplateProps> = ({ context }) => {
  if (process.env.GATSBY_SITE_ENVIRONMENT === SiteEnvironments.GLOBAL) {
    return <h1>Venue Page is unavailable on this environment.</h1>;
  }

  const page_name = context.name;

  const {
    hero_image,
    site_code,
    venue_logo,
    secondary_cta,
    page_footer,
    breadcrumb_override,
    has_background,
    zig_zag_content_left,
    zig_zag_content_right,
    service_cards,
    /*-- hardcoded venue fields (present only on Storyblok-only venue pages) --*/
    hc_venue_name,
    hc_status,
    hc_contact_number,
    hc_contact_email,
    hc_show_phone_number,
    hc_address_line1,
    hc_address_line2,
    hc_town_city,
    hc_county,
    hc_postcode,
    hc_local_authority,
    hc_region,
    hc_latitude,
    hc_longitude,
    hc_booking_link,
    hc_availability_link,
    hc_registration_link,
    hc_website_url,
    hc_terms_link,
    hc_holiday_schedule_link,
    hc_age_min,
    hc_age_max,
    hc_building_type_name,
    hc_pickup_dropoff_instructions,
    hc_service_list_info_title,
    hc_service_list_info_copy,
    hc_nearby_venue_ids,
    hc_services,
    hc_clubs,
  }: {
    SEO?: SEO;
    site_code: string;
    service_cards: ServiceCardProps[];
    hero_image: StoryblokAsset;
    venue_logo: StoryblokAsset;
    secondary_cta: ButtonProps[];
    page_footer: Blok[];
    breadcrumb_override: string;
    has_background: boolean;
    zig_zag_content_left: StoryblokAsset;
    zig_zag_content_right: StoryblokAsset;
  } & StoryblokHardcodedVenue = context.content;

  const field_component = context.field_component ?? "Page";

  const {
    header,
    venueHero,
    venueInfo,
    venueClubCards,
    venueNoDataFound,
    nearbyVenues,
    availabilityChecker,
    venueClubSchedule,
    zigZagLeft,
    zigZagRight,
    holidayProgramSchedule,
    newsletter,
    footer,
    countdowns,
    banners,
  } = useStaticQuery(graphql`
    {
      header: allStoryblokEntry(filter: { field_component: { eq: "Header" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      venueHero: allStoryblokEntry(filter: { field_component: { eq: "VenueHero" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      venueInfo: allStoryblokEntry(filter: { field_component: { eq: "VenueInfo" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      venueClubCards: allStoryblokEntry(filter: { field_component: { eq: "VenueClubCards" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      venueNoDataFound: allStoryblokEntry(filter: { field_component: { eq: "VenueNoDataFound" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      availabilityChecker: allStoryblokEntry(filter: { field_component: { eq: "AvailabilityChecker" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      venueClubSchedule: allStoryblokEntry(filter: { field_component: { eq: "VenueClubSchedule" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      nearbyVenues: allStoryblokEntry(filter: { field_component: { eq: "NearbyVenues" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      zigZagLeft: allStoryblokEntry(filter: { field_component: { eq: "ZigZagContentLeft" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      zigZagRight: allStoryblokEntry(filter: { field_component: { eq: "ZigZagContentRight" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      holidayProgramSchedule: allStoryblokEntry(filter: { field_component: { eq: "HolidayProgramSchedule" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      newsletter: allStoryblokEntry(filter: { field_component: { eq: "Newsletter" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      footer: allStoryblokEntry(filter: { field_component: { eq: "Footer" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      countdowns: allStoryblokEntry(sort: { fields: id }, filter: { field_component: { eq: "Countdown" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
    }
  `);

  // global bloks
  const headerBlok = getBlok(header);
  const footerBlok = getBlok(footer);
  const newsletterBlok = getBlok(newsletter);
  const breadcrumbs = useContext(LayoutContext);

  // Venue bloks
  const venueNoDataFoundBlok = getBlok(venueNoDataFound);
  const venueHeroBlok = getBlok(venueHero);
  const venueInfoBlok = getBlok(venueInfo);
  const venueClubCardsBlok = getBlok(venueClubCards);
  const venueClubScheduleBlok = getBlok(venueClubSchedule);
  const availabilityCheckerBlok = getBlok(availabilityChecker);
  const zigZagLeftBlok = getBlok(zigZagLeft);
  const zigZagRightBlok = getBlok(zigZagRight);
  const zigZagLightLeftBlok = {
    ...zigZagLeftBlok,
    component: "zig_zag_content_light",
    reversed: true,
  };
  const zigZagLightRightBlok = {
    ...zigZagRightBlok,
    component: "zig_zag_content_light",
    reversed: false,
  };
  const holidayProgramScheduleBlok = getBlok(holidayProgramSchedule);
  const nearbyVenuesBlok = getBlok(nearbyVenues);

  const updatedVenueClubCardsBlok = {
    ...venueClubCardsBlok,
    cards: venueClubCardsBlok.cards.map(card => {
      const match = service_cards.find(serviceCard => {
        return serviceCard.club?.toLowerCase().trim() === card.airtable_club_type?.toLowerCase().trim();
      });

      if (match && match.image) {
        return {
          ...card,
          image: match.image,
        };
      }

      return card;
    }),
  };

  const venues: Venue[] = useContext(VenuesContext);
  const { buildings, clubs } = useContext(FiltersContext);

  /*
   * Airtable-sourced venue lookup.
   * When site_code is blank or does not match any venue, `airtableVenue` will
   * be undefined and we fall back to building the venue from the hardcoded
   * Storyblok fields instead.
   */
  const airtableVenue: Venue | undefined = venues.filter((v: Venue) => v.id == site_code)[0];

  /*
   * Storyblok-hardcoded fallback.
   * mapStoryblokVenue returns null when hc_venue_name is absent, which
   * preserves the existing VenueNoDataFound behaviour for pages that genuinely
   * have neither an Airtable match nor hardcoded data.
   */
  const hardcodedVenue: Venue | null = !airtableVenue
    ? mapStoryblokVenue(
        {
          hc_venue_name,
          hc_status,
          hc_contact_number,
          hc_contact_email,
          hc_show_phone_number,
          hc_address_line1,
          hc_address_line2,
          hc_town_city,
          hc_county,
          hc_postcode,
          hc_local_authority,
          hc_region,
          hc_latitude,
          hc_longitude,
          hc_booking_link,
          hc_availability_link,
          hc_registration_link,
          hc_website_url,
          hc_terms_link,
          hc_holiday_schedule_link,
          hc_age_min,
          hc_age_max,
          hc_building_type_name,
          hc_pickup_dropoff_instructions,
          hc_service_list_info_title,
          hc_service_list_info_copy,
          hc_nearby_venue_ids,
          hc_services: hc_services ?? [],
          hc_clubs: hc_clubs ?? [],
        },
        context.full_slug,
      )
    : null;

  const venue: Venue | undefined = airtableVenue ?? hardcodedVenue ?? undefined;

  /*
   * Nearby venues resolution.
   * For Airtable-backed pages the standard context venues array is used.
   * For hardcoded pages the nearbyVenues array holds Airtable IDs so the
   * same lookup works transparently.
   */
  const venue_nearby_venues: Venue[] = venues.filter((loopedVenue: Venue) =>
    venue?.nearbyVenues?.includes(loopedVenue.id),
  );

  const getCountdownBlok = (content: any) => {
    const edges = content?.edges?.filter((edge: any) => edge.node.full_slug.includes(process.env.GATSBY_DOMAIN_FOLDER));

    if (!edges) return null;

    const allCountdownBlocks = edges.map((edge: any) => JSON.parse(edge.node.content));

    // Filter out countdowns that don't have this page uuid attached
    const countdownsForThisPageType = allCountdownBlocks.filter((countdown: any) =>
      countdown.show_on_content_types.includes(field_component),
    );

    // Take the highest priority countdown
    const countdownBlok = countdownsForThisPageType.sort((a: any, b: any) => a.priority - b.priority)[0];

    return countdownBlok;
  };

  const countdownBlok = getCountdownBlok(countdowns);

  return (
    <BaseDataLayer field_component={context.field_component}>
      <div className={layoutClassNames}>
        <header>{headerBlok && <DynamicComponent blok={headerBlok} />}</header>
        <main>
          {!venue && <VenueNoDataFound page_name={page_name} {...venueNoDataFoundBlok} />}
          {venue && (
            <>
              {/*-- venue hero --*/}
              <VenueHero
                venue={venue}
                hero_image={hero_image}
                {...venueHeroBlok}
                buildings={buildings}
                secondary_cta={secondary_cta}
              />

              {/*-- countdown blok --*/}
              {countdownBlok && <DynamicComponent blok={countdownBlok} />}

              {breadcrumbs && (
                <BreadcrumbBar
                  {...breadcrumbs}
                  breadcrumb_override={breadcrumb_override}
                  hasBackground={has_background}
                />
              )}
              {/*
            {/*-- venue info --*/}
              <VenueInfo venue={venue} venue_logo={venue_logo} {...venueInfoBlok} clubs={clubs} />

              {/*-- venue club schedule --*/}
              <VenueClubSchedule venue={venue} clubs={clubs} {...venueClubScheduleBlok} />
              {/* <SampleFeesTable venue={venue} clubs={clubs} {...venueClubScheduleBlok} /> */}

              {/*-- venue holiday program schedule --*/}
              <HolidayProgramSchedule venue={venue} {...holidayProgramScheduleBlok} />

              {/*-- venue clubs cards --*/}
              <VenueClubCards venue={venue} {...updatedVenueClubCardsBlok} />

              {/*-- venue availability checker --*/}
              <VenueAvailabilityChecker venue={venue} {...availabilityCheckerBlok} />

              {/*-- venue nearby clubs --*/}
              <VenueNearbyVenues
                venue={venue}
                nearby_venues={venue_nearby_venues}
                clubs={clubs}
                {...nearbyVenuesBlok}
              />

              {/*-- zig zags --*/}
              {zigZagLeftBlok && <ZigZagContentLight image={zig_zag_content_left} {...zigZagLightLeftBlok} />}
              {zigZagRightBlok && <ZigZagContentLight image={zig_zag_content_right} {...zigZagLightRightBlok} />}
            </>
          )}
          {page_footer && renderChildren(page_footer)}
        </main>
        <footer>
          {newsletterBlok && (
            <DynamicComponent blok={newsletterBlok} customProps={{ contentType: context.field_component }} />
          )}
          {footerBlok && <DynamicComponent blok={footerBlok} />}
        </footer>
      </div>
    </BaseDataLayer>
  );
};

export default VenuePageTemplate;
